import { useState, useEffect } from 'react';
import { UserPlus, Bug, Menu as MenuIcon } from 'lucide-react';
import Button from '../ui/Button';
import logo from '../../assets/logo.png';
import LanguageSwitcher from '../LanguageSwitcher';
import { useTranslation } from '../../context/LanguageContext';
import AddContactModal from './AddContactModal';
import { getStoredUser, isAdmin as checkIsAdmin } from '../../hooks/useAuth';

/**
 * Debug info component - shows auth token and storage key status
 */
const DebugInfo = () => {
    const [showDebug, setShowDebug] = useState(false);
    const [debugData, setDebugData] = useState({});
    const [fixing, setFixing] = useState(false);
    const [fixStatus, setFixStatus] = useState('');

    const updateDebug = () => {
        const authToken = localStorage.getItem('authToken') || '';
        const cabazaUser = localStorage.getItem('cabaza_user');
        let user = null;
        try {
            user = cabazaUser ? JSON.parse(cabazaUser) : null;
        } catch (e) {}

        setDebugData({
            token: authToken ? authToken.substring(0, 15) + '...' : 'MISSING',
            tokenType: authToken?.startsWith('eyJ') ? 'JWT' : (authToken ? 'placeholder' : 'none'),
            peopleUuid: user?.peopleUuid ? user.peopleUuid.substring(0, 8) + '...' : 'MISSING',
            storageKey: user?.storageKey ? 'OK' : 'MISSING',
            hasCabazaUser: !!user
        });
    };

    useEffect(() => {
        updateDebug();
        window.addEventListener('storage', updateDebug);
        return () => window.removeEventListener('storage', updateDebug);
    }, []);

    const handleFix = async () => {
        setFixing(true);
        setFixStatus('Fetching...');
        try {
            const userId = localStorage.getItem('userId');
            const userUuid = localStorage.getItem('userUuid');
            const userEmail = localStorage.getItem('userEmail');
            const userName = localStorage.getItem('userName');

            if (!userUuid) {
                setFixStatus('ERROR: userUuid missing');
                setFixing(false);
                return;
            }

            // Fetch user data from /peoples/{uuid}
            const response = await fetch(`https://apicabaza.creago.be/rest/cabaza/peoples/${userUuid}`);
            const text = await response.text();

            if (!text) {
                setFixStatus('ERROR: Empty response');
                setFixing(false);
                return;
            }

            let result;
            try {
                result = JSON.parse(text);
            } catch (parseErr) {
                setFixStatus('ERROR: Invalid JSON - ' + text.substring(0, 50));
                setFixing(false);
                return;
            }

            if (result.success || result.SUCCESS) {
                const userData = result.data || result.DATA;

                if (userData) {
                    const storageKey = userData.storagekey || userData.storageKey || userData.STORAGEKEY;
                    const peopleUuid = userData.peopleuuid || userData.peopleUuid || userData.PEOPLEUUID || userUuid;

                    // isAdmin can be 0, 1, true, false - need to check for undefined explicitly
                    let isAdmin = 0;
                    if (userData.isAdmin !== undefined) isAdmin = userData.isAdmin;
                    else if (userData.ISADMIN !== undefined) isAdmin = userData.ISADMIN;
                    else if (userData.isadmin !== undefined) isAdmin = userData.isadmin;

                    const cabazaUser = {
                        id: parseInt(userId, 10),
                        email: userEmail,
                        firstName: userData.firstname || userData.firstName || userName?.split(' ')[0] || '',
                        lastName: userData.lastname || userData.lastName || userName?.split(' ').slice(1).join(' ') || '',
                        peopleUuid: peopleUuid,
                        storageKey: storageKey || '',
                        isAdmin: isAdmin === 1 || isAdmin === '1' || isAdmin === true
                    };

                    localStorage.setItem('cabaza_user', JSON.stringify(cabazaUser));
                    setFixStatus(storageKey ? 'OK! Reload page' : 'OK but no storageKey');
                    updateDebug();
                } else {
                    setFixStatus('ERROR: User not found');
                }
            } else {
                setFixStatus('ERROR: ' + (result.message || 'API error'));
            }
        } catch (err) {
            setFixStatus('ERROR: ' + err.message);
        }
        setFixing(false);
    };

    return (
        <div className="relative">
            <button
                onClick={() => setShowDebug(!showDebug)}
                className={`p-1 rounded ${showDebug ? 'bg-yellow-200' : 'hover:bg-gray-100'}`}
                title="Debug info"
            >
                <Bug className={`w-4 h-4 ${debugData.tokenType === 'JWT' && debugData.storageKey === 'OK' ? 'text-green-500' : 'text-red-500'}`} />
            </button>
            {showDebug && (
                <div className="absolute top-8 left-0 z-50 bg-gray-900 text-xs font-mono p-3 rounded-lg shadow-xl min-w-[220px]">
                    <div className="space-y-1">
                        <div>
                            <span className="text-gray-400">token:</span>{' '}
                            <span className={debugData.tokenType === 'JWT' ? 'text-green-400' : 'text-red-400'}>
                                {debugData.token}
                            </span>
                            <span className={`ml-1 px-1 rounded text-[10px] ${debugData.tokenType === 'JWT' ? 'bg-green-800' : 'bg-red-800'}`}>
                                {debugData.tokenType}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">cabaza_user:</span>{' '}
                            <span className={debugData.hasCabazaUser ? 'text-green-400' : 'text-red-400'}>
                                {debugData.hasCabazaUser ? 'OK' : 'MISSING'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">peopleUuid:</span>{' '}
                            <span className={debugData.peopleUuid !== 'MISSING' ? 'text-purple-400' : 'text-red-400'}>
                                {debugData.peopleUuid}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">storageKey:</span>{' '}
                            <span className={debugData.storageKey === 'OK' ? 'text-green-400' : 'text-red-400'}>
                                {debugData.storageKey}
                            </span>
                        </div>
                        {(!debugData.hasCabazaUser || debugData.storageKey === 'MISSING') && (
                            <div className="pt-2 border-t border-gray-700 mt-2">
                                <button
                                    onClick={handleFix}
                                    disabled={fixing}
                                    className="w-full bg-yellow-600 hover:bg-yellow-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                                >
                                    {fixing ? 'Fixing...' : 'Fix cabaza_user'}
                                </button>
                                {fixStatus && (
                                    <div className={`mt-1 text-[10px] ${fixStatus.startsWith('OK') ? 'text-green-400' : 'text-red-400'}`}>
                                        {fixStatus}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Header Component
 * Displays the Potoo logo with baseline on the left and add contact button on the right
 * @param {Object} props
 * @param {Function} props.onContactAdded - Callback when a contact is added
 * @param {Function} props.onToggleMenu - Function to toggle mobile menu
 * @param {boolean} props.showMenuButton - Whether to show the menu button
 */
const Header = ({ onContactAdded, onToggleMenu, showMenuButton = false }) => {
    const { t } = useTranslation();
    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);

    // Get current user from localStorage using the hook
    const storedUser = getStoredUser();
    const currentUser = {
        id: storedUser?.id || parseInt(localStorage.getItem('userId') || '0', 10),
        uuid: storedUser?.peopleUuid || localStorage.getItem('userUuid') || '',
        email: storedUser?.email || localStorage.getItem('userEmail') || '',
        name: storedUser ? `${storedUser.firstName} ${storedUser.lastName}`.trim() : localStorage.getItem('userName') || ''
    };

    // Check if user is admin using the hook
    const isAdmin = checkIsAdmin();

    return (
        <div className="flex items-center justify-between mb-4 gap-2">
            {/* Left side: hamburger + logo */}
            <div className="flex items-center gap-2 min-w-0">
                {/* Mobile menu button */}
                {showMenuButton && (
                    <button
                        onClick={onToggleMenu}
                        className="sm:hidden flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-cabaza-purple hover:bg-gray-200 transition-colors"
                        aria-label="Ouvrir le menu"
                    >
                        <MenuIcon className="w-6 h-6" />
                    </button>
                )}
                <div className="flex items-end gap-2 sm:gap-3">
                    <img
                        src={logo}
                        alt="Potoo"
                        className="h-10 sm:h-15 object-contain flex-shrink-0"
                    />
                    <span className="hidden sm:block text-xs sm:text-sm font-semibold whitespace-nowrap bg-gradient-to-r from-cabaza-purple to-cabaza-pink bg-clip-text text-transparent pb-3">
                        Le réseau social européen pour échanger
                    </span>
                </div>
                {isAdmin && <DebugInfo />}
            </div>

            {/* Right side: language + add contact */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <LanguageSwitcher variant="compact" />
                <Button onClick={() => setIsAddContactModalOpen(true)} icon={UserPlus}>
                    <span className="hidden sm:inline">{t.dashboard?.addContact || 'Ajouter un contact'}</span>
                </Button>
            </div>

            {/* Add Contact Modal */}
            <AddContactModal
                isOpen={isAddContactModalOpen}
                onClose={() => setIsAddContactModalOpen(false)}
                currentUser={currentUser}
                onContactAdded={(contact) => {
                    onContactAdded?.(contact);
                    setIsAddContactModalOpen(false);
                }}
            />
        </div>
    );
};

export default Header;
